import type { ReactElement } from 'react';
import type { PageProps } from 'gatsby';
import { graphql } from 'gatsby';
import type {
  MarkdownRemarkFrontmatterAdvantagesCards,
  MarkdownRemarkFrontmatterCards,
  MarkdownRemarkFrontmatterCopy,
  MarkdownRemarkFrontmatterDeveloperCards,
  MarkdownRemarkFrontmatterFaqItems,
  MarkdownRemarkFrontmatterGlobalPayoutData,
  MarkdownRemarkFrontmatterPartners,
  MarkdownRemarkFrontmatterSolutionCards,
  UsHomepageQuery,
} from '../../../generated/types';
import { HomepageUsTemplate } from '../../components/templates/HomepageUsTemplate';
import { AdvantagesSection } from '../../modules/homepage/AdvantagesSection';
import { CardsSection } from '../../modules/homepage/CardsSection';
import { DevelopersSection } from '../../modules/homepage/DevelopersSection';
import { FaqSection } from '../../modules/homepage/FaqSection';
import { HeroSection } from '../../modules/homepage/HeroSection';
import { MapSection } from '../../modules/homepage/MapSection';
import { PartnersSection } from '../../modules/homepage/PartnersSection';
import { SolutionSection } from '../../modules/homepage/SolutionSection';

type PpUsPage = PageProps<UsHomepageQuery>;

function UsPage({ data: { globalPayout, partners, usHomepage } }: PpUsPage): ReactElement {
  return (
    <HomepageUsTemplate seo={usHomepage?.frontmatter?.seo}>
      <HeroSection
        buttonLabel1={usHomepage?.frontmatter?.heroButton1}
        buttonLabel2={usHomepage?.frontmatter?.heroButton2}
        buttonLink={usHomepage?.frontmatter?.heroButton2Link}
        subtitle={usHomepage?.frontmatter?.heroSubtitle}
        title={usHomepage?.frontmatter?.heroTitle}
      />
      <PartnersSection
        partners={partners?.frontmatter?.partners as Array<MarkdownRemarkFrontmatterPartners>}
      />
      <CardsSection
        cards={usHomepage?.frontmatter?.cards as Array<MarkdownRemarkFrontmatterCards>}
        textWithIconLabel={usHomepage?.frontmatter?.cardsTextWithIcon}
        title={usHomepage?.frontmatter?.cardsTitle}
      />
      <SolutionSection
        cards={
          usHomepage?.frontmatter?.solutionCards as Array<MarkdownRemarkFrontmatterSolutionCards>
        }
        imageLarge={usHomepage?.frontmatter?.imageLarge}
        imageSmall={usHomepage?.frontmatter?.imageSmall}
        subtitle={usHomepage?.frontmatter?.solutionSubtitle}
        textWithIconLabel={usHomepage?.frontmatter?.solutionTextWithIcon}
        title={usHomepage?.frontmatter?.solutionTitle}
      />
      <AdvantagesSection
        buttonLabel={usHomepage?.frontmatter?.advantagesButton}
        cards={
          usHomepage?.frontmatter
            ?.advantagesCards as Array<MarkdownRemarkFrontmatterAdvantagesCards>
        }
        image={usHomepage?.frontmatter?.advantagesImage}
        textWithIconLabel={usHomepage?.frontmatter?.advantageTextWithIcon}
        title={usHomepage?.frontmatter?.advantagesTitle}
      />
      <MapSection
        copy={globalPayout?.frontmatter?.copy as MarkdownRemarkFrontmatterCopy}
        data={
          globalPayout?.frontmatter
            ?.globalPayoutData as Array<MarkdownRemarkFrontmatterGlobalPayoutData>
        }
        subtitle={usHomepage?.frontmatter?.mapSubtitle}
        title={usHomepage?.frontmatter?.mapTitle}
      />
      <DevelopersSection
        buttonLabel1={usHomepage?.frontmatter?.developersButton1}
        buttonLabel2={usHomepage?.frontmatter?.developersButton2}
        cards={
          usHomepage?.frontmatter?.developerCards as Array<MarkdownRemarkFrontmatterDeveloperCards>
        }
        image={usHomepage?.frontmatter?.developersImage}
        subtitle={usHomepage?.frontmatter?.developersSubtitle}
        textWithIconLabel={usHomepage?.frontmatter?.developersTextWithIcon}
        title={usHomepage?.frontmatter?.developersTitle}
      />
      <FaqSection
        faqItems={usHomepage?.frontmatter?.faqItems as Array<MarkdownRemarkFrontmatterFaqItems>}
        subtitle={usHomepage?.frontmatter?.faqSubtitle}
        textWithIconLabel={usHomepage?.frontmatter?.faqTextWithIcon}
        title={usHomepage?.frontmatter?.faqTitle}
      />
    </HomepageUsTemplate>
  );
}

export const query = graphql`
  query UsHomepage {
    usHomepage: markdownRemark(frontmatter: { layout: { eq: "UsHomepage" } }) {
      frontmatter {
        seo {
          title
          description
          image
        }
        heroTitle
        heroSubtitle
        heroButton1
        heroButton2
        heroButton2Link
        cardsTitle
        cardsTextWithIcon
        cards {
          id
          title
          subtitle
        }
        solutionTitle
        solutionSubtitle
        solutionTextWithIcon
        solutionCards {
          id
          title
          subtitle
          button
          link
        }
        imageLarge {
          alt
          src
        }
        imageSmall {
          alt
          src
        }
        advantagesTitle
        advantageTextWithIcon
        advantagesButton
        advantagesImage {
          src
          alt
        }
        advantagesCards {
          id
          title
          subtitle
          image
        }
        mapTitle
        mapSubtitle
        developersTitle
        developersSubtitle
        developersTextWithIcon
        developersImage
        developersButton1
        developersButton2
        developerCards {
          id
          title
          subtitle
          image
        }
        faqTitle
        faqSubtitle
        faqTextWithIcon
        faqItems {
          faqCategory
          questions {
            id
            question
            answer
          }
        }
      }
    }
    partners: markdownRemark(frontmatter: { layout: { eq: "homepage" }, locale: { eq: "en" } }) {
      frontmatter {
        partners {
          id
          logo
          name
        }
      }
    }
    globalPayout: markdownRemark(
      frontmatter: { layout: { eq: "globalPayout" }, locale: { eq: "en" } }
    ) {
      frontmatter {
        copy {
          tab1
          tab2
          notAvailable
          tableHead1
          tableHead2
          tableHead3
        }
        globalPayoutData {
          country
          payoutChannels {
            id
            method
            processingTime
            currency
          }
          cardAcceptance {
            id
            method
            processingTime
            currency
          }
        }
      }
    }
  }
`;

export default UsPage;
